import React, { useState } from 'react';
import { BookOpen, TrendingUp, Target, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function LearningPathsSection() {
  // State to track which card is currently hovered or active (optional, set to null for hover only)
  const [activeCard, setActiveCard] = useState(null);

  const paths = [
    {
      id: 0,
      icon: <BookOpen className="w-6 h-6" />,
      bgColor: "bg-[#E6F4F1]",
      textColor: "text-[#0A4D3C]",
      title: "Mutual Funds & SIPs",
      desc: "Why diversified, boring investing quietly wins — and how to pick funds without a salesman.",
      lessons: 12,
      level: "Beginner",
      path: "/learning/paths/mutual-funds"
    },
    {
      id: 1,
      icon: <TrendingUp className="w-6 h-6" />,
      bgColor: "bg-[#E0F2FE]", // Light Blue
      textColor: "text-[#0369A1]",
      title: "Equity Foundations",
      desc: "How Indian markets actually work: exchanges, orders, valuations and your first stock.",
      lessons: 15,
      level: "Beginner",
      path: "/learning/paths/equity"
    },
    {
      id: 2,
      icon: <Target className="w-6 h-6" />,
      bgColor: "bg-[#F3E8FF]", // Light Purple
      textColor: "text-[#7E22CE]",
      title: "Trading with Discipline",
      desc: "Price action, risk-first strategy and journaling — process over predictions.",
      lessons: 14,
      level: "Intermediate",
      path: "/learning/paths/trading"
    },
    {
      id: 3,
      icon: <ShieldCheck className="w-6 h-6" />,
      bgColor: "bg-[#FEF3C7]", // Light Amber
      textColor: "text-[#D97706]",
      title: "Goal-Based Financial Planning",
      desc: "Insurance, tax-efficiency, retirement and goal planning — your full money map.",
      lessons: 10,
      level: "Beginner",
      path: "/learning/paths/planning"
    }
  ];

  return (
    <section id="learning" className="py-20 px-6 max-w-7xl mx-auto font-sans antialiased">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <h2 className="font-serif-title text-4xl font-bold text-[#0A2540] mb-3">
          Pick your starting point
        </h2>
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
          Four structured paths, one destination: money decisions you fully understand. Begin where you are today.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {paths.map((card, index) => (
          <div 
            key={card.id}
            onMouseEnter={() => setActiveCard(index)}
            onMouseLeave={() => setActiveCard(null)}
            className={`
              bg-white rounded-2xl p-6 border transition-all duration-300 ease-in-out cursor-pointer flex flex-col justify-between group
              ${activeCard === index 
                ? 'border-[#A3E0D3] shadow-2xl -translate-y-2' // Active/Hover Style
                : 'border-gray-200/80 shadow-sm hover:shadow-lg hover:-translate-y-0.5' // Normal/Initial Style
              }
            `}
          >
            <div className="flex-grow">
              {/* Dynamic Icon Background Color */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${card.bgColor} ${card.textColor}`}>
                {card.icon}
              </div>
              
              <h3 className="font-bold text-lg text-gray-900 mb-2 leading-tight">
                {card.title}
              </h3>
              
              <p className="text-xs text-gray-500 leading-relaxed mb-6">
                {card.desc}
              </p>
            </div>

            {/* Bottom Metadata Section - FIXED LAYOUT */}
            <div className="flex items-center justify-between text-xs font-semibold pt-4 border-t border-gray-100 mt-auto gap-2">
              {/* Combine Lessons & Level in a flex container to handle overflow */}
              <div className={`flex items-center gap-1.5 ${card.bgColor} ${card.textColor} px-2 py-1 rounded-full whitespace-nowrap`}>
                <span>📚 {card.lessons} Lessons</span>
                <span className="opacity-50">|</span>
                <span>{card.level}</span>
              </div>
              
              <Link 
                to={card.path} 
                className={`flex items-center gap-1 font-bold ${card.textColor} group-hover:underline whitespace-nowrap shrink-0`}
                onClick={(e) => e.stopPropagation()} 
              >
                Explore <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}